import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'

import './i18n/i18n'
import { LocaleProvider } from './i18n/LocaleProvider'
import { DirectionProvider } from './theme/DirectionProvider'
import { App } from './app/App'
import { ApiError } from './api/client'

/**
 * Query defaults tuned for the two very different kinds of data this app reads.
 *
 * Content changes a few times a year, so a long stale time avoids refetching cottage
 * descriptions every time someone switches tab. Availability opts out of this per query,
 * because a stale calendar is the one bug that costs the owner real money.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 404 or a validation failure just delays the error message.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <LocaleProvider>
        <DirectionProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </DirectionProvider>
      </LocaleProvider>
    </HelmetProvider>
  </StrictMode>,
)
